import { BarChart } from "@mui/x-charts";
import { Grid, Paper, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import widgetMetricMap, {
  funnelRatioMetricMap,
  profitAndFirstTimeDepositMetricMap,
} from "../../helpers/funcs/widgetMetricMap";
import { CustomItemTooltipBD } from "../../helpers/components/CustomTooltipBD";
import { useEffect, useState } from "react";
import { handleBarClickPopupforBars } from "../../helpers/funcs/handleBarClickPopupforBars";
import TrendsBreakdownPopupForBars from "../TrendsBreakdownPopup/TrendsBreakdownPopupForBars";
import { TrendsBreakdownQueryIds } from "../../helpers/funcs/trendsAllQueryIDs";

const TrendsBDCountry = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const fullWidgetName = searchParams.get("full_widget_name") || "";
  const metricNameFromUrl = searchParams.get("metric_name") || "";
  const valueSuffix = searchParams.get("value_suffix") || "";
  const {
    countryDataTrendsBreakdown,
    isLoadingBDAds,
    isChildrenAPILoadingBDAds,
  } = useSelector((state: any) => state.trends);

  const countryData =
    countryDataTrendsBreakdown?.data?.data?.query_output || [];

  const metricKey =
    fullWidgetName === "Funnel Ratio"
      ? funnelRatioMetricMap[metricNameFromUrl] || ""
      : fullWidgetName === "Profit and First Time Deposit"
      ? profitAndFirstTimeDepositMetricMap[metricNameFromUrl] || ""
      : widgetMetricMap[fullWidgetName];
  // Get unique countries with non-zero total CAC
  const totals: Record<string, number> = {};
  const groupedData = countryData.reduce((acc: any[], item: any) => {
    const timeUnit = item.time_unit;
    if (!timeUnit || isNaN(Date.parse(timeUnit))) return acc;

    const date = new Date(timeUnit);
    const formattedDate = format(date, "MMMM yyyy");

    const country = item.country;
    const value = +(
      (parseFloat(item[metricKey] ?? 0) || 0) * (valueSuffix === "%" ? 100 : 1)
    ).toFixed(2);

    if (!totals[country]) totals[country] = 0;
    totals[country] += value;

    let entry = acc.find((d) => d.x === formattedDate);
    if (!entry) {
      entry = { x: formattedDate };
      acc.push(entry);
    }

    entry[country] = (entry[country] || 0) + parseFloat(value.toFixed(2));
    entry[`click_in_${country}`] =
      (entry[`click_in_${country}`] || 0) + (item?.click_in || 0);
    return acc;
  }, []);

  const uniqueSources = Object.entries(totals)
    .filter(([source, total]) => source && !isNaN(total) && total !== 0)
    .sort((a, b) => b[1] - a[1]) // Descending sort
    .map(([source]) => source);

  const generateColor = (() => {
    const colorPalette = [
      "#6200EE",
      "#C8A1FF",
      "#2ECC71",
      "#F1C40F",
      "#3498DB",
      "#E67E22",
      "#1ABC9C",
      "#9B59B6",
      "#00BCD4",
      "#4CAF50",
      "#FFC107",
    ];
    const cache: Record<string, string> = {};
    return (source: string) => {
      if (!cache[source]) {
        const index = Object.keys(cache).length % colorPalette.length;
        cache[source] = colorPalette[index];
      }
      return cache[source];
    };
  })();

  const series = uniqueSources.map((source) => ({
    dataKey: source,
    color: generateColor(source),
    label: source.replace(/_/g, " "),
  }));

  const sortedData = groupedData.sort((a: any, b: any) => {
    const dateA = new Date(a.x + " 1");
    const dateB = new Date(b.x + " 1");
    return dateA.getTime() - dateB.getTime();
  });

  const [popupOpen, setPopupOpen] = useState(false);
  const [selectedBarDataC, setSelectedBarDataC] = useState<any>(null);

  const onBarClick = (_: any, params: any) => {
    if (isLoadingBDAds || isChildrenAPILoadingBDAds) return;
    handleBarClickPopupforBars({
      params,
      series,
      setSelectedBarData: setSelectedBarDataC,
      setPopupOpen,
      searchParams,
      section: "Country",
      queryID: TrendsBreakdownQueryIds.COUNTRY,
      setSearchParams,
    });
  };

  // Handle popup close
  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedBarDataC(null);
    const updatedParams = new URLSearchParams(searchParams);
    updatedParams.delete("section");
    updatedParams.delete("queryID");
    setSearchParams(updatedParams);
  };
  useEffect(() => {
    const section = searchParams.get("section");
    const queryID = searchParams.get("queryID");

    if (section || queryID) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete("section");
      updatedParams.delete("queryID");
      setSearchParams(updatedParams, { replace: true }); // avoid history entry
    }
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <Paper sx={{ marginBottom: "16px", boxShadow: "none" }}>
        <Grid container sx={{ padding: "16px 24px" }}>
          <Grid
            item
            xs={12}
            md={10}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <Box>
              <Typography variant="body1" sx={{ fontWeight: "medium" }}>
                Breakdown by Country
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}></Grid>
        </Grid>
        <Box>
          <BarChart
            data-tracker="487"
            dataset={sortedData}
            series={series}
            xAxis={[{ scaleType: "band", dataKey: "x", disableLine: true }]}
            yAxis={[
              {
                dataKey: "y",
                disableLine: true,
                disableTicks: true,
              },
            ]}
            leftAxis={null}
            rightAxis={{}}
            height={380}
            margin={{ left: 30, right: 56, top: 25, bottom: 84 }}
            grid={{ vertical: false, horizontal: true }}
            onItemClick={onBarClick}
            slotProps={{
              legend: {
                direction: "row",
                position: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                labelStyle: {
                  fontSize: 12,
                },
                itemMarkWidth: 12,
                itemMarkHeight: 2,
                markGap: 8,
                itemGap: 10,
                padding: 28,
              },
            }}
            sx={{
              [`& .MuiChartsAxis-tickLabel`]: {
                fill: "grey !important",
              },
              [`& .MuiChartsAxis-tick`]: {
                stroke: "grey !important",
              },
              [`& .MuiChartsLegend-root text`]: {
                fill: "grey !important",
              },
              [`& *`]: {
                cursor:
                  isLoadingBDAds || isChildrenAPILoadingBDAds
                    ? "wait !important"
                    : "pointer !important", // 🧠 dynamic cursor
              },
            }}
          >
            <CustomItemTooltipBD
              dataset={sortedData}
              valueSuffix={valueSuffix}
            />
          </BarChart>
        </Box>
      </Paper>
      <TrendsBreakdownPopupForBars
        open={popupOpen}
        onClose={handleClosePopup}
        data={selectedBarDataC}
      />
    </>
  );
};

export default TrendsBDCountry;
